

import config from '../../config';
import {
  CallStatus, OrderSideEnum, OrderStatusEnum, TimeInForceEnum,
} from '../enums';
import { OrderTypeEnum } from '../enums/order-type.enum';
import lang from '../language';
import { SORExtendedHoursMarker, SOROrderRequestDataBase, SOROrderSide, SOROrderType } from '../models/gateway/types';

import { Order } from '../models/trading/types';
import { SubscribeGroup } from '../store/market-data/types';
import { ModifyOrCancelOrderRequest } from '../store/trading/types';

import { getProp, getPropName } from './ObjectTools';
import { TextHelpers } from './TextHelpers';
import {
  INVALID_VALUE,
  NullableString,
  OrderTicketComponent,
} from './types';

export function getMarketDataTopic(response: any): string | null {
  return (response.topic
    || ((getProp(response, '0') === 'heartbeat') && 'heartbeat')
    || getPropName(response, 'query')
    || getPropName(response, 'subscribe')
    || getPropName(response, 'login')
  );
}

export function orderStatusToCallStatus(orderStatus?: OrderStatusEnum | null) {
  let callStatus: CallStatus;

  switch (orderStatus) {
    case OrderStatusEnum.Accepted:
    case OrderStatusEnum.Queued:
    case OrderStatusEnum.PartiallyFilled:
    case OrderStatusEnum.Filled:
    case OrderStatusEnum.Cancelled:
    case OrderStatusEnum.Modified:
      callStatus = CallStatus.READY;
      break;

    case OrderStatusEnum.Rejected:
    case OrderStatusEnum.TimedOut:
      callStatus = CallStatus.ERROR;
      break;

    default:
      callStatus = CallStatus.INITIAL;
      break;
  }

  return callStatus;
}

export const mapOrderSORtoSide = (sorSide: SOROrderSide) => {
  switch (sorSide) {
    case SOROrderSide.Buy:
      return OrderSideEnum.Buy;
    case SOROrderSide.Sell:
      return OrderSideEnum.Sell;
    case SOROrderSide.SellShort:
      return OrderSideEnum.SellShort;

    default:
      console.warn(`[TradingHelpers] mapOrderSide - unknown input value - ${sorSide} - using 'Buy' by default`);
      return OrderSideEnum.Buy;
  }
};

export const getOrderDestination = (destination?: NullableString): string => {
  if (destination) {
    return destination;
  }

  const isProdEnv = config.currentEnv === 'prod';
  return isProdEnv ? 'INCA' : 'DEMO';
};

export function setSOROrderData(data: Order | ModifyOrCancelOrderRequest) {
  const {
    destination,
    symbol,
    clientOrderId,
    orderQty,
    ordType,
    side = OrderSideEnum.Buy,
    stopPrice,
    price,
    traceIdentifier,
  } = data as Order;
  let { order, account } = data as ModifyOrCancelOrderRequest;
  if (!order) account = (data as ModifyOrCancelOrderRequest).account;
  const orderData: SOROrderRequestDataBase = {
    clOrdId: clientOrderId!,
    orderQty: orderQty!,
    exDestination: getOrderDestination(destination),
    account,
    side,
    symbol: symbol!,
    timeInForce: TimeInForceEnum.EndOfDay,
    traceIdentifier,
    tradingSessionId: 'M' as SORExtendedHoursMarker,
  };

  switch (ordType) {
    case OrderTypeEnum.Market:
      orderData.type = 'MarketOrder';
      break;
    case OrderTypeEnum.Limit:
      orderData.type = 'LimitOrder';
      orderData.price = price;
      break;
    case OrderTypeEnum.Stop:
      orderData.type = 'StopOrder';
      orderData.stopPx = stopPrice;
      break;
    default:
      console.error(`[Global.service] Error setting order type -- ${ordType}`);
      break;
  }

  return orderData;
}

export const parseOrderTicketComponentToSubscribeGroup = (component: OrderTicketComponent): SubscribeGroup | null => {
  let group: SubscribeGroup | null = null;
  switch (component) {
    case 'initial':
      group = component;
      break;

    case 'watchlist/popular':
    case 'watchlist/favorites':
    case 'watchlist':
      group = 'watchlist';
      break;

    case 'discover':
    case 'discover/epic-movers':
    case 'discover/week52-gainers-losers':
    case 'discover/hot-gappers':
    case 'discover/top-movers':
    case 'discover/popular-group':
      group = 'discover';
      break;

    case 'my-account':
    case 'my-account/positions':
    case 'my-account/orders':
    case 'my-account/account-breakdown':
      group = 'my-account';
      break;

    case 'history': {
      group = component;
      break;
    }

    case 'people-also-trade':
      group = component;
      break;

    case 'symbol-details': {
      group = 'symbol-details';
      break;
    }

    case 'chart-details': {
      group = 'chart-details';
      break;
    }

    case 'create-order': {
      group = 'create-order';
      break;
    }

    case 'modify-order': {
      group = 'modify-order';
      break;
    }

    case 'funds': {
      group = null;
      break;
    }

    case 'profile': {
      group = null;
      break;
    }

    default:
      break;
  }

  return group;
};

export const getOrderStatusLabelByName = (orderStatus: NullableString): string => {
  switch (orderStatus) {
    case OrderStatusEnum.Filled:
      return lang.commonOrderStatusFilterFilled();
    case OrderStatusEnum.Cancelled:
      return lang.commonOrderStatusCanceled();
    case OrderStatusEnum.Rejected:
      return lang.commonOrderStatusRejected();
    case OrderStatusEnum.PartiallyFilled:
      return lang.commonOrderStatusFilterPartiallyFilled();
    case OrderStatusEnum.PartiallyReject:
      return lang.commonOrderStatusFilterPartiallyRejected();
    case OrderStatusEnum.Expired:
    case OrderStatusEnum.OtherwiseExpired:
      return lang.commonOrderStatusFilterExpired();
    default:
      return '';
  }
};

export const mapOrderStatus = (orderStatus?: NullableString): string => {
  if (!orderStatus) return INVALID_VALUE;

  let result = '';
  const asArray = orderStatus.split(' ');
  asArray.forEach((word, index) => {
    result += (index > 0 ? ' ' : '') + word.charAt(0).toUpperCase() + word.substr(1);
  });

  return result;
};

export const mapOrderType = (side: SOROrderSide, type: SOROrderType, isInPendingOrderTable = false): string => {
  if (!type || !side) return INVALID_VALUE;

  let result: string = TextHelpers.getOrderTypeName(SOROrderType[type]);

  switch (side) {
    case SOROrderSide.Buy:
      result += ` ${lang.commonOrderSideButtonBuy()}`;
      break;
    case SOROrderSide.Sell:
      result += ` ${lang.commonOrderSideButtonSell()}`;
      break;
    case SOROrderSide.SellShort:
      result += isInPendingOrderTable ? ` ${lang.commonShortSell()}` : ` ${lang.commonOrderSideButtonSell()}`;
      break;
    default:
      break;
  }

  return result;
};

export const convertOrderSideToText = (side: OrderSideEnum|NullableString): string => {
  switch (side) {
    case OrderSideEnum.Buy: {
      return lang.commonOrderSideButtonBuy();
    }
    case OrderSideEnum.Sell: {
      return lang.commonOrderSideButtonSell();
    }
    case OrderSideEnum.SellShort: {
      return lang.commonShortSell2();
    }
    default: return '';
  }
};
